import React from 'react'
import PropTypes from 'prop-types'
import {Box, Image, Text, useMultiStyleConfig, Link as ChakraLink} from '@chakra-ui/react'
import {Link as RouteLink} from 'react-router-dom'
import Link from '@salesforce/retail-react-app/app/components/link'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import {createGradientBackground} from '../../theme/components/project/list-menu-card'
import {motion} from 'framer-motion'

const coverStyles = {objectFit: 'cover', w: '100%', h: '100%'}

const MenuCard = ({
    title,
    subtitle,
    secondaryTitle,
    url,
    variant = 'default',
    image,
    svg,
    isAbsolute = false,
    isLink = false,
    fullWidth = false,
    handleClick = noop,
    customTitleColor,
}) => {
    const styles = useMultiStyleConfig('KenwoodMenuCard', {variant: variant})

    const isGradientVariant =
        variant === 'filter' || variant === 'promo' || variant === 'exploreAll'
    const shouldAnimate =
        variant === 'default' || variant === 'defaultLarge' || variant === 'exploreAll'

    const handleCardClick = (e) => {
        handleClick(e)
    }

    const cardVariants = {
        rest: {scale: 1},
        hover: {scale: 0.95},
    }

    const imageVariants = {
        rest: {scale: 1},
        hover: {scale: 1.1},
    }

    const Card = (
        <Box
            {...styles.container}
            onClick={handleCardClick}
            as={motion.div}
            w={fullWidth ? '100%' : styles?.container?.w}
            variants={shouldAnimate ? cardVariants : {}}
            initial="rest"
            whileHover="hover"
            transition="0.3s ease"
        >
            <Box
                as={motion.div}
                variants={shouldAnimate ? imageVariants : {}}
                transition="0.3s ease"
                {...styles.image}
                sx={
                    isGradientVariant && image?.url
                        ? {
                              ...styles.image,
                              ...createGradientBackground(image.url),
                          }
                        : styles.image
                }
            >
                {!isGradientVariant && image && (
                    <Image src={image?.url} alt={title} sx={coverStyles} />
                )}
                {!image && svg}
            </Box>
            <Text
                {...styles.title}
                color={customTitleColor ? customTitleColor : styles?.title?.color}
            >
                {title}
            </Text>
            {subtitle && <Text {...styles.subtitle}>{subtitle}</Text>}
            {secondaryTitle && <Text {...styles.secondaryTitle}>{secondaryTitle}</Text>}
        </Box>
    )

    if (!isLink) {
        return Card
    }

    if (isAbsolute) {
        return (
            <ChakraLink
                href={url}
                _hover={{textDecoration: 'none'}}
                isExternal
                target="_blank"
                rel="noreferrer"
            >
                {Card}
            </ChakraLink>
        )
    }

    return (
        <Link as={RouteLink} to={url} _hover={{textDecoration: 'none'}}>
            {Card}
        </Link>
    )
}

MenuCard.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    secondaryTitle: PropTypes.string,
    url: PropTypes.string,
    image: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }),
    fullWidth: PropTypes.bool,
    svg: PropTypes.element,
    isAbsolute: PropTypes.bool,
    isLink: PropTypes.bool,
    customTitleColor: PropTypes.string,
    handleClick: PropTypes.func,
    variant: PropTypes.oneOf(['default', 'filter', 'promo', 'secondary', 'simpleLink', 'variant']),
}

export {MenuCard}
